import React, { useCallback, useRef, useState } from "react";
import {
  Button,
  makeStyles,
  createStyles,
  Typography,
  Fab,
} from "@material-ui/core";
import { red } from "@material-ui/core/colors";
import { useHistory } from "react-router-dom";

import AlertDialog from "../common/AlertDialog";
import ErrorDialog from "../common/ErrorDialog";
import Loading from "../common/Loading";
import TopBar from "../common/TopBar";
import PasswordField from "../common/PasswordField";
import { changePassword } from "../../helper/Api";

import KeyIcon from "@material-ui/icons/Lock";
import { errors, getApiError } from "../../helper/Error";

// CSSスタイルを定義
const useStyles = makeStyles((theme) =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      alignItems: "center",
      minHeight: "calc(100vh - 58px)", // topbarの高さ分を引く(progressBar込み)
      [`${theme.breakpoints.up("xs")} and (orientation: landscape)`]: {
        minHeight: "calc(100vh - 50px)",
      },
      [theme.breakpoints.up("sm")]: {
        minHeight: "calc(100vh - 66px)",
      },
    },
    icon: {
      marginTop: theme.spacing(5),
      marginBottom: theme.spacing(5),
    },
    title: {
      width: "78%",
      maxWidth: "350px",
      marginBottom: theme.spacing(1),
    },
    text: {
      width: "78%",
      maxWidth: "350px",
      fontSize: 18,
      marginBottom: theme.spacing(3),
    },
    oldPassword: {
      marginBottom: theme.spacing(1),
      width: "78%",
      maxWidth: "350px",
    },
    newPassword: {
      marginBottom: theme.spacing(1),
      width: "78%",
      maxWidth: "350px",
    },
    newPassAgain: {
      marginBottom: theme.spacing(2),
      width: "78%",
      maxWidth: "350px",
    },
    flashMsg: {
      height: theme.spacing(6),
      marginBottom: theme.spacing(2),
      width: "78%",
      maxWidth: "350px",
      color: red[800],
      fontSize: 16,
    },
    button: {
      width: "78%",
      maxWidth: "350px",
      marginBottom: theme.spacing(5),
    },
  })
);

export default function PassChange() {
  const classes = useStyles();
  const history = useHistory();

  const oldPass = useRef("");
  const newPass = useRef("");
  const newPassAgain = useRef("");
  const [flashMsg, setFlashMsg] = useState("");
  const [isActiveButton, setIsActiveButton] = useState(false);

  const errorRef = useRef({});
  const [isOpenError, setIsOpenError] = useState(false);
  const handleCloseError = () => {
    setIsOpenError(false);
  };

  const [isOpenResult, setIsOpenResult] = useState(false);
  const handleCloseResult = useCallback(() => {
    setIsOpenResult(false);
    history.push("/calendar");
  }, [history]);

  const validatePassword = useCallback(() => {
    if (newPass.current.length < 8) {
      setFlashMsg("パスワードは8文字以上入力してください");
      setIsActiveButton(false);
      return;
    }

    if (newPassAgain.current === "") {
      setFlashMsg("確認用のパスワードを入力してください");
      setIsActiveButton(false);
      return;
    }

    if (newPass.current !== newPassAgain.current) {
      setFlashMsg("入力されたパスワードと一致しません");
      setIsActiveButton(false);
      return;
    }

    if (newPass.current === newPassAgain.current) {
      setFlashMsg("");
      setIsActiveButton(true);
      return;
    }
  }, []);

  const handleChangeOldPass = useCallback((e) => {
    oldPass.current = e.target.value;
  }, []);
  const handleChangeNewPass = useCallback(
    (e) => {
      newPass.current = e.target.value;

      validatePassword();
    },
    [validatePassword]
  );
  const handleChangeNewPassAgain = useCallback(
    (e) => {
      newPassAgain.current = e.target.value;

      validatePassword();
    },
    [validatePassword]
  );

  const handleClickButton = useCallback(() => {
    const fn = async () => {
      setIsChanging(true);

      await changePassword(oldPass.current, newPass.current)
        .then((res) => {
          if (res.data.error.length === 0) {
            setIsOpenResult(true);
          } else throw res.data.error;
        })
        .catch((err) => {
          errorRef.current = err.code
            ? getApiError(err)
            : errors.APP_CONNECTION_FAILED;
          setIsOpenError(true);
        });

      setIsChanging(false);
    };
    fn();
  }, []);

  const [isChanging, setIsChanging] = useState(false);

  return (
    <>
      <TopBar />

      <div className={classes.root}>
        <Fab
          className={classes.icon}
          color="primary"
          disableRipple
          disableFocusRipple
        >
          <KeyIcon />
        </Fab>
        <Typography variant="h6" className={classes.title}>
          パスワード変更
        </Typography>

        <Typography className={classes.text} color="textSecondary">
          現在のパスワードと新しいパスワードを入力してください
        </Typography>

        <PasswordField
          label="現在のパスワード"
          className={classes.oldPassword}
          onChange={handleChangeOldPass}
        />

        <PasswordField
          label="新しいパスワード"
          className={classes.newPassword}
          onChange={handleChangeNewPass}
        />

        <PasswordField
          label="新しいパスワード確認"
          className={classes.newPassAgain}
          onChange={handleChangeNewPassAgain}
        />

        <Typography className={classes.flashMsg}>{flashMsg}</Typography>

        <Button
          variant="contained"
          color="primary"
          className={classes.button}
          onClick={handleClickButton}
          disabled={!isActiveButton}
        >
          変更する
        </Button>
      </div>

      <AlertDialog open={isOpenResult} onClose={handleCloseResult}>
        正常に変更されました
      </AlertDialog>

      <ErrorDialog
        open={isOpenError}
        onClose={handleCloseError}
        error={errorRef.current}
      />

      <Loading open={isChanging} />
    </>
  );
}
